import { BanknotesIcon } from "@heroicons/react/24/outline";
import React from "react";
import type { MindspaceEventFragment } from "~/graphql/codegen/graphql";
import { EventState } from "~/graphql/codegen/graphql";
import MindspaceEventDetails from "./mindspace-event-details";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogTrigger,
  toast,
} from "@mindstonehq/ui";
import { CalendarIcon, ShareIcon } from "lucide-react";
import MindspaceEventRSVPDialog from "~/components/events/mindspace-event-rsvp-dialog";
import copyToClipboard from "copy-to-clipboard";
import { Link } from "react-router";
import { mayCheckInOthers } from "~/utils/mindspace-permission";
import { useCurrentSpace } from "~/hooks/context-hook";
import AddToCalendar from "../add-to-calendar";
import metrics from "~/utils/metrics";
import { getSpotsLeftScarcityLabel } from "~/utils/spots-left-scarcity-helper";
import {
  LiveEventCTAButton,
  type LiveEventCTAState,
} from "~/components/events/live-event-cta-banner";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function MindspaceEventRSVP({
  event,
  liveEventCta,
}: {
  event: MindspaceEventFragment;
  liveEventCta?: LiveEventCTAState | null;
}) {
  const { t } = useLingui();
  const handleShare = async () => {
    const url = window.location.href;
    toast(t(msg`Event link copied to clipboard`));
    const notify = () => {
      toast(t(msg`Event link copied to clipboard`));
    };
    const shareData = {
      title: `${event.name}`,
      text: t(msg`Check out this event on Mindstone`),
      url,
    };
    if (navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData).catch((err) => {
        console.error(err);
        copyToClipboard(url);
        notify();
      });
    } else {
      copyToClipboard(url);
      notify();
    }
  };
  const currentSpace = useCurrentSpace();
  const canScan = mayCheckInOthers(currentSpace.currentUserPermission);

  const location =
    event.location?.__typename === "OnlineLocation"
      ? event.location.joinUrl
      : event.location?.__typename === "GeoLocation"
        ? event.location.address
        : null;

  const isPaid = Boolean(
    event.price?.isPaid && (event.price?.amountMinor ?? 0) > 0,
  );
  const priceLabel = event.price?.display ?? "";
  const isRegistered =
    event.attending || event.approvalStatus === "confirmed";
  // Paid + not-yet-registered must route through the Stripe checkout loader
  // (/events/{slug}/rsvp) instead of the free in-place RSVP dialog.
  const showPaidCheckoutCta = isPaid && !isRegistered;
  // FOX-6587: a reader outside a cohort-only session's audience gets no
  // join, RSVP or add-to-calendar action; the details show the notice instead.
  const locked = event.lockedForViewer;

  return (
    <Card>
      <CardHeader>
        <CardTitle><Trans>Details</Trans></CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col space-y-3">
          <div className="flex items-center h-8 space-x-1">
            <BanknotesIcon className="flex-none h-4 w-4 text-tertiary" />
            <div className="t-body-sm-medium text-muted-foreground">
              {isPaid ? (
                priceLabel ? <span>{priceLabel}</span> : <Trans>Paid</Trans>
              ) : (
                <Trans>Free</Trans>
              )}
              {(() => {
                const label = getSpotsLeftScarcityLabel(
                  event.attendance.capacity ?? 0,
                  event.attendance.registered ?? 0,
                  event.attendance.available ?? null,
                );
                return label.label ? <> - {label.label}</> : null;
              })()}
            </div>
          </div>
          <MindspaceEventDetails event={event} />
        </div>
      </CardContent>
      <CardFooter className={"flex gap-3 flex-wrap "}>
        {!locked && liveEventCta && (
          <LiveEventCTAButton state={liveEventCta} />
        )}
        <Button variant="outline" size="sm" onClick={() => handleShare()}>
          <ShareIcon className="h-5" />
        </Button>
        {locked ? null : event.state === EventState.Past ? (
          <Button variant="secondary" size="sm" disabled>
            <Trans>Unavailable</Trans>
          </Button>
        ) : event.attendance.available !== 0 ? (
          <div className="flex gap-2 flex-wrap">
            {showPaidCheckoutCta ? (
              <Link to={`/events/${event.slug}/rsvp`}>
                <Button variant="secondary" size="sm">
                  {priceLabel
                    ? t(msg`Register · ${priceLabel}`)
                    : t(msg`Register`)}
                </Button>
              </Link>
            ) : (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant={
                    event.attending ||
                    event.approvalStatus === "confirmed" ||
                    event.approvalStatus === "pending" ||
                    event.approvalStatus === "approved"
                      ? "outline"
                      : "secondary"
                  }
                  size="sm"
                >
                  {event.approvalStatus === "pending"
                    ? t(msg`Your request is being reviewed`)
                    : event.approvalStatus === "approved"
                      ? t(msg`Approved`)
                      : event.approvalStatus === "rejected"
                        ? t(msg`Not approved`)
                        : event.approvalStatus === "cancelled"
                          ? t(msg`Cancelled`)
                          : event.attending ||
                              event.approvalStatus === "confirmed"
                            ? t(msg`Attending`)
                            : event.requiresApproval
                              ? t(msg`Request to join`)
                              : t(msg`Register`)}
                </Button>
              </DialogTrigger>
              <MindspaceEventRSVPDialog event={event} />
            </Dialog>
            )}

            {(event.attending || event.approvalStatus === "confirmed") && (
              <AddToCalendar
                events={[
                  {
                    id: event.id,
                    name: event.name,
                    startDatetime: event.startDatetime,
                    endDatetime: event.endDatetime,
                    description: event.eventDescription,
                    joinUrl: location,
                    publicUrl: event.publicUrl,
                    agenda:
                      event.agenda?.map((x) => ({ title: x.title })) || [],
                  },
                ]}
                downloadFilename={event.name}
                variant="outline"
                size="sm"
                className="flex gap-2"
                track={() => {
                  metrics.track("live_session_calendar", {
                    page: "event_rsvp",
                    action: "add_event",
                  });
                }}
              >
                <CalendarIcon className="flex-none h-4 w-4" />
                <Trans>Add to calendar</Trans>
              </AddToCalendar>
            )}
          </div>
        ) : (
          <>
            {event.attending || event.approvalStatus === "confirmed" ? (
              <AddToCalendar
                events={[
                  {
                    id: event.id,
                    name: event.name,
                    startDatetime: event.startDatetime,
                    endDatetime: event.endDatetime,
                    description: event.eventDescription,
                    joinUrl:
                      event.location?.__typename === "OnlineLocation"
                        ? event.location.joinUrl
                        : event.locationShort,
                    publicUrl: event.publicUrl,
                    agenda:
                      event.agenda?.map((x) => ({ title: x.title })) || [],
                  },
                ]}
                downloadFilename={event.name}
                variant="outline"
                size="sm"
                className="flex gap-2"
                track={() => {
                  metrics.track("live_session_calendar", {
                    page: "event_rsvp",
                    action: "add_event",
                  });
                }}
              >
                <CalendarIcon className="flex-none h-4 w-4" />
                <Trans>Add to calendar</Trans>
              </AddToCalendar>
            ) : (
              <Button variant="secondary" size="sm" disabled>
                <Trans>Sold out!</Trans>
              </Button>
            )}
          </>
        )}
        {canScan && (
          <Link
            className="w-full md:hidden"
            to={`/event-scanner/${event.slug}`}
          >
            <Button className="w-full" size="sm">
              <Trans>Scan Attendees</Trans>
            </Button>
          </Link>
        )}
      </CardFooter>
    </Card>
  );
}
